// Compatibility names for existing callers. This module manages local targeting
// identity only: no LaunchDarkly SDK, flag snapshots, network, or analytics.
import { isEqual, merge } from 'lodash-es';
import uuid from 'uuid/v4';

import { legacyFlags } from 'state/datadog-flags/legacy-flags';
import { loadLanguage } from 'state/intl/load-language';
import { loadRegion } from 'state/intl/load-region';
import { PROD_DEFAULT_LANGUAGE } from 'state/intl/supported-languages';
import { PROD_DEFAULT_REGION } from 'state/intl/supported-regions';
import { appVersionCode, platform, sanityDataset } from 'utils/environment';
import LocalStorage from 'utils/local-storage';
import { Keys } from 'utils/local-storage/constants';

import { FlagType, LaunchDarklyFlag } from './flags';
import { LDUser } from './user';

export * from './flags';
export type { LDUser } from './user';
export type LDFlagSet = Record<string, any>;
export type LaunchDarklyFlagsObject = { [F in LaunchDarklyFlag]?: FlagType<F> };
export type UserAttributeUpdates = LDUser;
export type AnonymousUserAttributes = LDUser & { anonymous: true };
export type UserAttributes = LDUser;

let inMemoryAnonymousKey: string | undefined;
const safeRead = (key: Keys): any => {
  try {
    return LocalStorage.getItem(key);
  } catch {
    return undefined;
  }
};
const anonymousKey = () => {
  // ld:$anonUserId is a raw browser string, unlike the application's JSON cache.
  let original: string | null = null;
  try {
    original = window.localStorage.getItem('ld:$anonUserId');
  } catch {
    // A stable in-memory identity still permits evaluation with blocked storage.
  }
  let cached: string | null = null;
  try {
    cached = LocalStorage.getItem(Keys.LEGACY_FLAGS_ANONYMOUS_ID);
  } catch {
    // Storage can be disabled or exhausted; retain the process-local identity.
  }
  const key = cached || original || inMemoryAnonymousKey || uuid();
  inMemoryAnonymousKey = key;
  LocalStorage.setItem(Keys.LEGACY_FLAGS_ANONYMOUS_ID, key);
  return key as string;
};

const createAnonymousUserAttributes = (deviceId?: string): LDUser => {
  const key = anonymousKey();
  let region = PROD_DEFAULT_REGION;
  let language = PROD_DEFAULT_LANGUAGE[region];
  try {
    region = loadRegion();
    language = loadLanguage();
  } catch {
    // Use the application's locale defaults if browser storage is unavailable.
  }
  return normalizeUserAttributes({
    key,
    anonymous: true,
    custom: {
      host: window.location.host,
      platform: platform(),
      mobileOS: '',
      device_id: deviceId ?? '',
      userClient: window.navigator.userAgent || '',
      appVersion: appVersionCode(),
      appShellVersion: '',
      appFlowBuildId: '',
      language,
      sanityDataset: `${sanityDataset}_${region.toLowerCase()}`,
    },
    country: region,
  });
};

export default class LaunchDarklyHelper {
  private static instance: LaunchDarklyHelper;
  private _userAttributes: LDUser = {};

  public get datadogUserAttributes(): LDUser {
    return this._userAttributes;
  }

  public get userAttributes() {
    return this._userAttributes;
  }

  public async initUserAttributes(hydratedUserAttributes: LDUser = {}) {
    // Preserve the old persisted anonymous key without loading the old SDK.
    if (hydratedUserAttributes.anonymous && hydratedUserAttributes.key) {
      inMemoryAnonymousKey = hydratedUserAttributes.key;
      LocalStorage.setItem(Keys.LEGACY_FLAGS_ANONYMOUS_ID, hydratedUserAttributes.key);
    }
    this._userAttributes = merge({}, createAnonymousUserAttributes(), hydratedUserAttributes);
    this.persist();
    return this.userAttributes;
  }

  private persist() {
    LocalStorage.setItem(Keys.LEGACY_FLAGS_USER_ATTRIBUTES, this._userAttributes);
  }

  public async updateCurrentUser(changes: UserAttributeUpdates) {
    const normalized = normalizeUserAttributes(changes);
    // Changing account keys must not carry another account's targeting data.
    // Rebuild app/anonymous attributes and preserve only the independent device id.
    const switching = normalized.key !== undefined && normalized.key !== this._userAttributes.key;
    const original = switching
      ? createAnonymousUserAttributes(this._userAttributes?.custom?.device_id)
      : this._userAttributes;
    const attributes = merge({}, original, normalized);
    if (!isEqual(this._userAttributes, attributes)) {
      this._userAttributes = attributes;
      this.persist();
    }
    // Desired identity changes immediately; the adapter bounds and serializes
    // configuration reconciliation. Caller-specific reads never mutate this store.
    legacyFlags.update(this.datadogUserAttributes);
    return { userAttributes: this._userAttributes };
  }

  public clearCurrentUser = async () => {
    this._userAttributes = createAnonymousUserAttributes(
      this._userAttributes?.custom?.device_id as string | undefined
    );
    this.persist();
    legacyFlags.update(this.datadogUserAttributes);
    return { userAttributes: this._userAttributes };
  };

  public static getInstance(): LaunchDarklyHelper {
    if (!LaunchDarklyHelper.instance) {
      LaunchDarklyHelper.instance = new LaunchDarklyHelper();
    }
    return LaunchDarklyHelper.instance;
  }

  public static async init() {
    const helper = this.getInstance();
    // The legacy cache is identity migration input only, never flag defaults.
    const cached =
      safeRead(Keys.LEGACY_FLAGS_USER_ATTRIBUTES) ||
      safeRead(Keys.LAUNCH_DARKLY_USER_ATTRIBUTES) ||
      {};
    await helper.initUserAttributes(cached);
    return helper;
  }
}

const trimStringKeys = <T extends {}>(attributes: T): T =>
  Object.entries(attributes).reduce((acc, [key, value]) => {
    if (typeof value === 'object') {
      return { ...acc, [key]: value && trimStringKeys(value) };
    }

    if (typeof value === 'string') {
      return { ...acc, [key]: value.trim() };
    }

    return { ...acc, [key]: value };
  }, {} as T);

const emailAttributeLowerCase = ({ email, ...attributes }: UserAttributeUpdates) => ({
  ...attributes,
  ...(email ? { email: email.toLowerCase() } : null),
});

export const normalizeUserAttributes = (userAttributeUpdates: UserAttributeUpdates): LDUser =>
  emailAttributeLowerCase(trimStringKeys(userAttributeUpdates));
